import type { ExcelAction } from "./types";
import type { ExtractionResult } from "../dialog-host";
import { openPdfImporter } from "../dialog-host";
import { emitEvent } from "../../lib/telemetry";
import { reportError } from "../../lib/errorLog";

export const importPdf: ExcelAction = {
  id: "import-pdf",
  label: "Import PDF",
  description: "Send a PDF to the extraction server and write the result into a new sheet.",
  async run({ log }) {
    const startedAt = performance.now();
    emitEvent({ event_name: "PDF_IMPORT_STARTED", feature_code: "import-pdf" });

    log("opening PDF importer…");
    let result: ExtractionResult | null;
    try {
      result = await openPdfImporter();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      log(`PDF import failed: ${msg}`, "error");
      emitEvent({
        event_name: "PDF_IMPORT_FAILED",
        feature_code: "import-pdf",
        duration_ms: Math.round(performance.now() - startedAt),
        error_code: "PDF_EXTRACT_FAILED",
      });
      reportError({ error_code: "PDF_EXTRACT_FAILED", error: err });
      return;
    }

    if (!result) {
      log("cancelled");
      emitEvent({
        event_name: "PDF_IMPORT_CANCELLED",
        feature_code: "import-pdf",
        duration_ms: Math.round(performance.now() - startedAt),
      });
      return;
    }

    log(
      `received: ${result.filename}, ${result.pages} page(s), ` +
        `${result.tables.length} table(s), ${result.ocr_text.length} OCR block(s)`,
      "success"
    );
    await insertExtractionIntoNewSheet(result, log);
    emitEvent({
      event_name: "PDF_IMPORT_COMPLETED",
      feature_code: "import-pdf",
      duration_ms: Math.round(performance.now() - startedAt),
    });
  },
};

type Logger = (message: string, level?: "info" | "success" | "error") => void;

async function insertExtractionIntoNewSheet(result: ExtractionResult, log: Logger): Promise<void> {
  const sheetName = buildSheetName(result.filename);

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.add(sheetName);
    sheet.activate();

    const rows: Array<Array<string | number | boolean>> = [];

    rows.push(["filename", result.filename]);
    rows.push(["pages", result.pages]);
    rows.push(["sha256", result.sha256]);
    rows.push(["imported_at", new Date().toISOString()]);
    rows.push([""]);

    if (result.text.some((p) => p.lines.length > 0)) {
      rows.push(["--- TEXT ---"]);
      for (const page of result.text) {
        if (page.lines.length === 0) continue;
        rows.push([`Page ${page.page}`]);
        for (const line of page.lines) {
          rows.push(["", line]);
        }
        rows.push([""]);
      }
    }

    if (result.tables.length > 0) {
      rows.push(["--- TABLES ---"]);
      for (const table of result.tables) {
        rows.push([`Page ${table.page}, Table ${table.index + 1}`]);
        for (const row of table.rows) {
          rows.push(row.map((c) => c ?? ""));
        }
        rows.push([""]);
      }
    }

    if (result.ocr_text.length > 0) {
      rows.push(["--- OCR ---"]);
      for (const page of result.ocr_text) {
        rows.push([`Page ${page.page} (OCR)`]);
        for (const line of page.lines) {
          rows.push(["", line]);
        }
        rows.push([""]);
      }
    }

    const maxCols = rows.reduce((m, r) => Math.max(m, r.length), 1);
    const padded = rows.map((r) => {
      const out: Array<string | number | boolean> = r.slice();
      while (out.length < maxCols) out.push("");
      return out;
    });

    if (padded.length === 0) {
      padded.push(["(no content extracted)"]);
      while (padded[0].length < maxCols) padded[0].push("");
    }

    const range = sheet.getRangeByIndexes(0, 0, padded.length, maxCols);
    range.values = padded;
    sheet.getRange("A:A").format.autofitColumns();

    await context.sync();
  });

  log(`inserted into sheet "${sheetName}"`, "success");
}

function buildSheetName(filename: string): string {
  // Excel sheet names: max 31 chars, can't include : \ / ? * [ ]
  const stem = filename.replace(/\.pdf$/i, "");
  const cleaned = stem.replace(/[:\\/?*[\]]/g, "_");
  const truncated = cleaned.length > 24 ? cleaned.slice(0, 24) : cleaned;
  const suffix = new Date().toISOString().slice(11, 19).replace(/:/g, "");
  return `PDF ${truncated} ${suffix}`.slice(0, 31);
}
